import { useEffect, useId, useRef, useState } from 'react';
import { resolveTac, resolveMarketTac, tacMarketAllowsCalculation, tacConstituents, tacPackageKey, type TacAdjustment } from '../utils/tac';
import './TacValueEditor.css';

type Props = {
  coa: unknown;
  adjustment?: TacAdjustment;
  onChange?: (adjustment: TacAdjustment) => void;
  calculationEnabled?: boolean;
};

export default function TacValueEditor({ coa, adjustment = {}, onChange, calculationEnabled = true }: Props) {
  const resolution = resolveMarketTac(coa, calculationEnabled ? adjustment : undefined, calculationEnabled);
  const [addKey, setAddKey] = useState(tacConstituents[0].key);
  const [addValue, setAddValue] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const fieldId = useId();
  const breakdownId = useId();
  const { edited: _edited, ...recipe } = adjustment;
  const state = resolution.provenance === 'reported' ? 'Reported by Metrc'
    : resolution.provenance === 'edited' ? 'Edited'
    : resolution.provenance === 'calculated' ? `Calculated by Retail ID${resolution.adjusted ? ' - adjusted' : ''}` : 'Unavailable';
  // Check source data without recipe edits so excluded analytes can still be restored.
  const hasConstituentData = resolveTac(coa).calculated !== null;
  const editable = calculationEnabled && tacMarketAllowsCalculation(coa) && hasConstituentData && Boolean(onChange) && resolution.provenance !== 'reported';
  const breakdown = [...resolution.breakdown].sort((first, second) =>
    (second.percent ?? -1) - (first.percent ?? -1));
  const includedCount = breakdown.filter(row => row.contribution !== null).length;
  const formatPercentage = (value: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(value);
  const displayedTotal = resolution.value === null ? ''
    : resolution.provenance === 'calculated' ? Number(resolution.value.toPrecision(12)) : resolution.value;
  const [draftTotal, setDraftTotal] = useState<string | null>(null);
  const editTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editContext = JSON.stringify([tacPackageKey(coa), adjustment, resolution.value, editable]);

  useEffect(() => {
    setDraftTotal(null);
    setError('');
    return () => {
      if (editTimer.current !== null) clearTimeout(editTimer.current);
      editTimer.current = null;
      if (!editable) return;
    };
  }, [editContext]);

  function commitTotal(value: string) {
    if (editTimer.current !== null) clearTimeout(editTimer.current);
    editTimer.current = null;
    const edited = Number(value);
    if (value !== '' && (!Number.isFinite(edited) || edited < 0 || edited > 100)) {
      setError('Enter a percentage between 0 and 100.');
      return;
    }
    setError('');
    setDraftTotal(null);
    onChange?.(value === '' ? { ...recipe, recalculate: true } : { ...recipe, edited });
  }

  return (
    <section className={`tac-editor tac-editor--${resolution.provenance}`} data-testid="tac-value-editor" aria-label="Total Active Cannabinoids">
      <div className="tac-editor-header">
        <label className="tac-editor-title" htmlFor={fieldId}>Total Active Cannabinoids (%)</label>
        <div className="tac-editor-total">
          <input id={fieldId} data-testid="tac-value" aria-label="Edit TAC percentage"
            type="number" min="0" max="100" step="any" readOnly={!editable}
            placeholder="Unavailable" value={draftTotal ?? displayedTotal}
            onChange={event => {
              if (!editable) return;
              const value = event.target.value;
              setDraftTotal(value);
              setError('');
              if (editTimer.current !== null) clearTimeout(editTimer.current);
              editTimer.current = setTimeout(() => commitTotal(value), 500);
            }}
            onBlur={() => { if (draftTotal !== null) commitTotal(draftTotal); }}
            onKeyDown={event => {
              if (event.key === 'Enter' && draftTotal !== null) {
                event.preventDefault();
                commitTotal(draftTotal);
              }
            }} />
          <span className="tac-editor-source" data-testid="tac-source" role="status">{state}</span>
          {resolution.provenance !== 'reported' && (
            <button className="tac-editor-toggle" type="button" aria-label="TAC breakdown"
              title="TAC breakdown" aria-expanded={expanded} aria-controls={breakdownId}
              onClick={() => setExpanded(open => !open)}>
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          )}
        </div>
        {!tacMarketAllowsCalculation(coa) && <p role="note">TAC calculation is not enabled for this package market. Only Metrc-reported TAC is available.</p>}
      </div>
      {resolution.provenance !== 'reported' && expanded && (
        <div className="tac-editor-breakdown" id={breakdownId}>
          <h3>{resolution.provenance === 'edited' ? 'Edited total; calculation shown below' : 'Calculated from the analytes below'}</h3>
          <p className="tac-editor-description">These are the cannabinoids the TAC formula expects. Reported values are checked and filled in; add the rest that apply.</p>
          <details className="tac-editor-formula">
            <summary>Formula</summary>
            <p>Sum of neutral % + sum of (acid % &times; 0.877)</p>
          </details>
          <div className="tac-editor-list-heading">
            <strong>Proposed analytes</strong>
            <span>{includedCount} in total</span>
          </div>
          <ul className="tac-editor-analytes">
            {breakdown.map(row => (
              <li className={`tac-editor-analyte${row.contribution === null ? ' tac-editor-analyte--excluded' : ''}`}
                key={row.key} data-testid={`tac-analyte-${row.key}`}>
                <input className="tac-editor-include" type="checkbox" aria-label={`Include ${row.label}`}
                  checked={row.contribution !== null} disabled={!editable || row.percent === null}
                  onChange={event => onChange?.({
                    ...recipe,
                    excluded: event.target.checked
                      ? recipe.excluded?.filter(key => key !== row.key)
                      : [...(recipe.excluded ?? []), row.key],
                  })} />
                <div className="tac-editor-analyte-identity">
                  <strong>{row.label}</strong>
                  {row.factor !== 1 && <span className="tac-editor-factor">&times;{row.factor}</span>}
                  {row.percent !== null && (
                    <span className="tac-editor-reported">
                      {Object.prototype.hasOwnProperty.call(recipe.added ?? {}, row.key) ? 'Added' : 'Reported'}
                    </span>
                  )}
                  {row.reason && <span className="tac-editor-reason">{row.reason}</span>}
                </div>
                <div className="tac-editor-measurement">
                  <input type="number" aria-label={`${row.label} percentage`} value={row.percent ?? ''}
                    placeholder="—" readOnly />
                  <span>%</span>
                </div>
                <span className="tac-editor-contribution" title={row.contribution === null ? row.reason : `${row.contribution}%`}>
                  {row.contribution === null ? <>&mdash;</> : `${formatPercentage(row.contribution)}%`}
                </span>
              </li>
            ))}
          </ul>
          <p className="tac-editor-calculated">Retail ID would calculate: <strong>{resolution.calculated === null ? 'Unavailable' : `${formatPercentage(resolution.calculated)}%`}</strong></p>
          {resolution.calculated === null && <p className="tac-editor-description">
            {hasConstituentData
              ? 'Restore an analyte or reset the breakdown to calculate a total.'
              : 'TAC editing and insertion require at least one valid reported constituent percentage or a Metrc-reported TAC value.'}
          </p>}
          {editable && (
            <>
              <div className="tac-editor-add">
                <label>
                  Cannabinoid
                  <select aria-label="Cannabinoid to add" value={addKey} onChange={event => setAddKey(event.target.value)}>
                    {tacConstituents.map(row => <option key={row.key} value={row.key}>{row.label}</option>)}
                  </select>
                </label>
                <label>
                  Percentage to add
                  <input aria-label="Percentage to add" type="number" min="0" max="100" step="any" value={addValue} onChange={event => setAddValue(event.target.value)} />
                </label>
                <button className="button secondary" type="button" onClick={() => {
                  const value = Number(addValue);
                  if (!addValue.trim() || !Number.isFinite(value) || value < 0 || value > 100) {
                    setError('Enter a percentage between 0 and 100.');
                    return;
                  }
                  setError('');
                  onChange?.({ ...recipe, excluded: recipe.excluded?.filter(key => key !== addKey), added: { ...recipe.added, [addKey]: value } });
                  setAddValue('');
                }}>Add value</button>
              </div>
              <div className="tac-editor-actions">
                <button className="button secondary" type="button" onClick={() => { setError(''); onChange?.({ ...recipe, recalculate: true }); }}>Revert to calculated</button>
                {resolution.adjusted && <button className="button secondary" type="button" onClick={() => { setError(''); onChange?.({ recalculate: true }); }}>Reset breakdown</button>}
              </div>
            </>
          )}
        </div>
      )}
      {error && <div className="tac-editor-error" role="alert">{error}</div>}
    </section>
  );
}
